/** Per-org preference keys for operational context (e.g. last property on Amenities). */

export const HOME_PATH_DASHBOARD = '/dashboard';
export const HOME_PATH_FRONT_DESK = '/front-desk';

const homePathKey = (userId: string) => `entrio.context.homePath.${userId}`;

function readHomePath(userId?: string): string | null {
  if (!userId) return null;
  try {
    return localStorage.getItem(homePathKey(userId));
  } catch {
    return null;
  }
}

export function getHomePath(userId?: string, roleName?: string): string {
  const saved = readHomePath(userId);
  if (saved === HOME_PATH_FRONT_DESK || saved === HOME_PATH_DASHBOARD) {
    return saved;
  }
  if (roleName === 'Org Operator') return HOME_PATH_FRONT_DESK;
  return HOME_PATH_DASHBOARD;
}

export function isFrontDeskHome(userId?: string, roleName?: string): boolean {
  return getHomePath(userId, roleName) === HOME_PATH_FRONT_DESK;
}

type HomePathListener = () => void;
const homePathListeners = new Set<HomePathListener>();

export function subscribeHomePath(listener: HomePathListener) {
  homePathListeners.add(listener);
  return () => {
    homePathListeners.delete(listener);
  };
}

export function setHomePath(userId: string, path: string) {
  try {
    localStorage.setItem(homePathKey(userId), path);
    homePathListeners.forEach(listener => listener());
  } catch {
    /* quota / private mode */
  }
}

const frontDeskIntroKey = (userId: string) => `entrio.whatsNew.frontDesk.${userId}`;

export function isFrontDeskIntroDismissed(userId?: string): boolean {
  if (!userId) return true;
  try {
    return localStorage.getItem(frontDeskIntroKey(userId)) === '1';
  } catch {
    return true;
  }
}

export function dismissFrontDeskIntro(userId: string) {
  try {
    localStorage.setItem(frontDeskIntroKey(userId), '1');
  } catch {
    /* quota / private mode */
  }
}

const frontDeskStartPageHintKey = (userId: string) => `entrio.whatsNew.frontDeskStartPage.${userId}`;

export function isFrontDeskStartPageHintDismissed(userId?: string): boolean {
  if (!userId) return true;
  try {
    return localStorage.getItem(frontDeskStartPageHintKey(userId)) === '1';
  } catch {
    return true;
  }
}

export function dismissFrontDeskStartPageHint(userId: string) {
  try {
    localStorage.setItem(frontDeskStartPageHintKey(userId), '1');
  } catch {
    /* quota / private mode */
  }
}

const amenitiesPropertyKey = (orgId: string) => `entrio.context.amenitiesProperty.${orgId}`;

export function getLastAmenitiesPropertyId(orgId: string): string | null {
  try {
    return localStorage.getItem(amenitiesPropertyKey(orgId));
  } catch {
    return null;
  }
}

export function setLastAmenitiesPropertyId(orgId: string, propertyId: string | null) {
  try {
    const key = amenitiesPropertyKey(orgId);
    if (propertyId) {
      localStorage.setItem(key, propertyId);
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    /* quota / private mode */
  }
}
